/* Native-scroll gallery with scoped, disposable Shopify editor controllers. */
(() => {
  "use strict";
  if (window.__ppaGalleryReady) return;
  window.__ppaGalleryReady = true;
  const reduced = matchMedia("(prefers-reduced-motion: reduce)");
  const mobile = matchMedia("(max-width:700px)");
  const controllers = new Map();
  const selector = "[data-ppa-cinema], [data-ppa-story], [data-ppa-works]";
  const clamp = (value) => Math.max(0, Math.min(1, value));
  const smooth = (value) => {
    const x = clamp(value);
    return x * x * (3 - 2 * x);
  };
  // Rule 3: no hard-coded brand colours — read the design-system tokens
  // (assets/ppa-base.css :root) once and reuse the resolved values.
  let ppaTokens = null;
  function ppaToken(name, fallback) {
    if (!ppaTokens) ppaTokens = getComputedStyle(document.documentElement);
    const value = ppaTokens.getPropertyValue(name).trim();
    return value || fallback;
  }
  let frame = 0;
  let header = null;
  let headerSize = 92;
  const headerObserver = new ResizeObserver(schedule);

  function measureHeader() {
    const next = document.querySelector(".shopify-section--header");
    if (next !== header) {
      headerObserver.disconnect();
      header = next;
      if (header) headerObserver.observe(header);
    }
    const nextSize = header?.getBoundingClientRect().height || 92;
    if (
      nextSize !== headerSize ||
      !document.documentElement.style.getPropertyValue("--ppa-header-height")
    ) {
      headerSize = nextSize;
      document.documentElement.style.setProperty(
        "--ppa-header-height",
        `${headerSize}px`,
      );
    }
  }
  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      measureHeader();
      for (const [root, controller] of controllers) {
        if (!root.isConnected) {
          controller.destroy();
          controllers.delete(root);
        } else controller.update();
      }
      document.body.classList.toggle(
        "ppa-intro-active",
        [...controllers.values()].some((controller) => controller.introActive),
      );
    });
  }
  function initialize(root) {
    if (controllers.has(root)) return;
    const events = new AbortController();
    const observers = [];
    const updates = [];
    const cleanups = [];
    const controller = {
      introActive: false,
      update: () => updates.forEach((update) => update()),
      destroy: () => {
        events.abort();
        cleanups.forEach((cleanup) => cleanup());
        observers.forEach((observer) => observer.disconnect());
      },
      selectBlock: () => {},
    };
    const on = (target, name, handler, options = {}) =>
      target?.addEventListener(name, handler, {
        ...options,
        signal: events.signal,
      });
    const observe = (element) => {
      if (!element) return;
      const observer = new ResizeObserver(schedule);
      observer.observe(element);
      observers.push(observer);
    };
    const motionDisabled = () =>
      reduced.matches || root.dataset.motion === "false";

    if (root.matches("[data-ppa-cinema]")) {
      const wrap = root.querySelector(".film-scroll");
      const stage = wrap?.querySelector(".film-stage");
      const photo = wrap?.querySelector(".film-photo");
      if (wrap && stage && photo) {
        // Native scroll timelines stay synchronized with momentum scrolling.
        // Build numeric transform/opacity keyframes only when geometry changes;
        // do not drive the mobile artwork through main-thread scroll callbacks.
        let nativeAnimations = [];
        let nativeDirty = true;
        let nativeFailed = false;
        let nativeGeometry = "";
        const cancelNative = () => {
          nativeAnimations.forEach((animation) => animation.cancel());
          nativeAnimations = [];
          wrap.classList.remove("native-cinema");
        };
        const invalidateNative = () => {
          nativeDirty = true;
          schedule();
        };
        const nativeObserver = new ResizeObserver(invalidateNative);
        nativeObserver.observe(stage);
        observers.push(nativeObserver);
        on(photo, "load", invalidateNative);
        on(window, "resize", invalidateNative, { passive: true });
        on(mobile, "change", invalidateNative);
        on(reduced, "change", invalidateNative);
        cleanups.push(cancelNative);
        function buildNative(disabled) {
          const enabled =
            !disabled &&
            !nativeFailed &&
            mobile.matches &&
            typeof window.ViewTimeline === "function" &&
            typeof photo.animate === "function";
          if (!enabled) {
            cancelNative();
            nativeGeometry = "";
            nativeDirty = true;
            return false;
          }
          if (!nativeDirty) return nativeAnimations.length > 0;
          nativeDirty = false;
          const height = stage.clientHeight;
          const width = stage.clientWidth;
          if (!photo.naturalWidth || !photo.naturalHeight || !height)
            return false;
          const geometry = [
            height,
            width,
            headerSize,
            photo.naturalWidth,
            photo.naturalHeight,
          ].join(":");
          if (geometry === nativeGeometry) return nativeAnimations.length > 0;
          cancelNative();
          nativeGeometry = geometry;
          const baseWidth =
            (height * 0.68 * photo.naturalWidth) / photo.naturalHeight;
          const zoom = Math.max(width / baseWidth, 1 / 0.68) * 1.08;
          const timeline = new ViewTimeline({
            subject: wrap,
            axis: "block",
            inset: `${headerSize}px calc(100% - ${height + headerSize}px)`,
          });
          const frames = Array.from({ length: 101 }, (_, index) => {
            const progress = index / 100;
            const travel = smooth((progress - 0.44) / 0.4);
            const scale =
              (zoom - (zoom - 1) * smooth(progress / 0.62)) *
              (1 - (travel * 0.24) / 0.68);
            const copy = smooth((progress - 0.62) / 0.25);
            return {
              offset: progress,
              photo: `translate(-50%, -50%) translateY(${travel * height * 0.14}px) scale(${scale})`,
              panel: `translateY(${(1 - smooth((progress - 0.5) / 0.28)) * -101}%)`,
              copy,
              hint: 1 - smooth(progress / 0.25),
            };
          });
          const animate = (element, keyframes) => {
            if (element)
              nativeAnimations.push(
                element.animate(keyframes, {
                  timeline,
                  rangeStart: "contain 0%",
                  rangeEnd: "contain 100%",
                  fill: "both",
                  easing: "linear",
                }),
              );
          };
          wrap.classList.add("native-cinema");
          animate(
            photo,
            frames.map((f) => ({ offset: f.offset, transform: f.photo })),
          );
          animate(
            wrap.querySelector(".film-panel"),
            frames.map((f) => ({ offset: f.offset, transform: f.panel })),
          );
          animate(
            wrap.querySelector(".film-copy"),
            frames.map((f) => ({
              offset: f.offset,
              opacity: f.copy,
              transform: `translateY(${(1 - f.copy) * 16}px)`,
            })),
          );
          animate(
            wrap.querySelector(".film-caption"),
            frames.map((f) => ({ offset: f.offset, opacity: f.hint })),
          );
          animate(wrap.querySelector(".film-progress i"), [
            { transform: "scaleX(0)" },
            { transform: "scaleX(1)" },
          ]);
          return true;
        }
        function updateNative(disabled) {
          try { return buildNative(disabled); }
          catch (error) {
            cancelNative();
            nativeFailed = true;
            return false;
          }
        }
        updates.push(() => {
          const disabled = motionDisabled();
          wrap.classList.toggle("static", disabled);
          const stageRect = stage.getBoundingClientRect();
          controller.introActive =
            stageRect.bottom > innerHeight * 0.55 &&
            stageRect.top < innerHeight * 0.5;
          if (updateNative(disabled)) return;
          const rect = wrap.getBoundingClientRect();
          const distance = wrap.offsetHeight - stage.offsetHeight;
          const progress = disabled
            ? 1
            : clamp(distance > 0 ? (headerSize - rect.top) / distance : 1);
          const p = smooth(progress / 0.62);
          const travel = smooth((progress - 0.44) / 0.4);
          const baseHeight = Math.max(1, stage.clientHeight * 0.68);
          const baseWidth =
            baseHeight * (photo.naturalWidth / (photo.naturalHeight || 1));
          const zoom = mobile.matches
            ? Math.max(
                stage.clientWidth / (baseWidth || stage.clientWidth || 1),
                stage.clientHeight / baseHeight,
              ) * 1.08
            : Math.max(
                2.9,
                (stage.clientWidth /
                  (photo.offsetWidth || stage.clientWidth || 1)) *
                  1.03,
              );
          wrap.style.setProperty("--mobile-settle", travel);
          wrap.style.setProperty("--scale", zoom - (zoom - 1) * p);
          wrap.style.setProperty("--art-x", `${50 + 25 * travel}%`);
          wrap.style.setProperty("--panel", smooth((progress - 0.5) / 0.28));
          wrap.style.setProperty("--copy", smooth((progress - 0.62) / 0.25));
          wrap.style.setProperty("--hint", 1 - smooth(progress / 0.25));
          wrap.style.setProperty("--reveal", progress);
          wrap.dataset.progress = progress.toFixed(3);
        });
        on(photo, "load", schedule);
      }
    }
    if (root.matches("[data-ppa-story]")) {
      const region = root.querySelector(".story-region");
      const finance = root.querySelector(".financing");
      if (region && finance)
        updates.push(() => {
          const disabled = motionDisabled();
          root.classList.toggle("color-static", disabled);
          if (disabled) return;
          const rect = finance.getBoundingClientRect();
          const enter = clamp(
            (innerHeight * 0.9 - rect.top) / (innerHeight * 0.9),
          );
          const leave = clamp(
            (innerHeight * 0.72 - rect.bottom) / (innerHeight * 0.72),
          );
          const progress = enter * (1 - leave);
          const shade = Math.round(250 - 227 * smooth(progress));
          region.style.setProperty(
            "--scene-bg",
            `${shade},${shade},${Math.max(0, shade - 2)}`,
          );
          region.style.setProperty(
            "--scene-ink",
            shade > 117 ? "#000" : "#fff",
          );
          region.style.setProperty(
            "--scene-num",
            shade > 117
              ? ppaToken("--ppa-ink", "#171717")
              : ppaToken("--ppa-yellow", "#ffd931"),
          );
          region.dataset.night = progress.toFixed(3);
        });
      const entries = [...root.querySelectorAll(".review-entry")];
      const count = root.querySelector("[data-review-count]");
      const previous = root.querySelector("[data-review-prev]");
      const next = root.querySelector("[data-review-next]");
      let active = Math.min(2, entries.length - 1);
      function show(index) {
        if (!entries.length) return;
        active = (index + entries.length) % entries.length;
        entries.forEach((entry, i) => {
          entry.hidden = i !== active;
        });
        if (count)
          count.textContent = `${String(active + 1).padStart(2, "0")} / ${String(entries.length).padStart(2, "0")}`;
        if (previous) previous.disabled = entries.length < 2;
        if (next) next.disabled = entries.length < 2;
      }
      on(previous, "click", () => show(active - 1));
      on(next, "click", () => show(active + 1));
      controller.selectBlock = (blockId) => {
        const index = entries.findIndex(
          (entry) => entry.dataset.reviewId === blockId,
        );
        if (index >= 0) show(index);
      };
      show(active);
    }
    if (root.matches("[data-ppa-works]")) {
      const rail = root.querySelector(".art-rail");
      const previous = root.querySelector("[data-works-prev]");
      const next = root.querySelector("[data-works-next]");
      const line = root.querySelector(".rail-line i");
      if (rail && previous && next) {
        const status = () => {
          const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
          previous.disabled = rail.scrollLeft < 5;
          next.disabled = rail.scrollLeft >= max - 5;
          if (line)
            line.style.transform = `translateX(${max ? (100 * rail.scrollLeft) / max : 0}%)`;
        };
        const move = (direction) => {
          const card = rail.querySelector(".art-card");
          if (card)
            rail.scrollBy({
              left:
                direction *
                (card.getBoundingClientRect().width +
                  parseFloat(getComputedStyle(rail).gap || 0)),
              behavior: reduced.matches ? "instant" : "smooth",
            });
        };
        on(previous, "click", () => move(-1));
        on(next, "click", () => move(1));
        on(rail, "scroll", status, { passive: true });
        observe(rail);
        updates.push(status);
      }
    }
    controllers.set(root, controller);
    controller.update();
  }
  function mount(container) {
    if (container.matches?.(selector)) initialize(container);
    container.querySelectorAll(selector).forEach(initialize);
    schedule();
  }
  document.addEventListener("shopify:section:load", (event) =>
    mount(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) => {
    for (const [root, controller] of controllers) {
      if (event.target === root || event.target.contains(root)) {
        controller.destroy();
        controllers.delete(root);
      }
    }
    schedule();
  });
  document.addEventListener("shopify:section:reorder", schedule);
  document.addEventListener("shopify:block:select", (event) => {
    const root = event.target.closest("[data-ppa-story]");
    controllers.get(root)?.selectBlock(event.detail.blockId);
  });
  addEventListener("scroll", schedule, { passive: true });
  addEventListener("resize", schedule, { passive: true });
  reduced.addEventListener("change", schedule);
  mobile.addEventListener("change", schedule);
  measureHeader();
  mount(document);
})();
